import { inject, injectable } from "inversify";
import { action, computed, makeObservable, observable } from "mobx";
import type { IAutoSyncActivitiesDataLayer } from "../types/IAutoSyncActivitiesDataLayer";
import { fromPromise } from "mobx-utils";
import { AutoSyncActivitiesDataLayerSymbol } from "./AutoSyncActivitiesDataLayerSymbol";
import type { IntervalOptionType } from "../types/IntervalOptionType";
import { INTERVAL_OPTIONS } from "./IntervalOptions";

@injectable()
export class AutoSyncActivitiesSettingsStore {
  selectedOption: IntervalOptionType = INTERVAL_OPTIONS[0];
  isRunning = false;
  isSaving = false;
  nextRun = "";
  previousRun = "";

  constructor(@inject(AutoSyncActivitiesDataLayerSymbol) private autoSyncActivitiesDataLayer: IAutoSyncActivitiesDataLayer) {
    makeObservable(this, {
      selectedOption: observable.deep,
      isRunning: observable,
      isSaving: observable,
      nextRun: observable,
      previousRun: observable,
      setSelectedOption: action,
      setIsRunning: action,
      setIsSaving: action,
      runNow: action,
      saveSettings: action,
      init: computed,
    });
  }

  get init() {
    return fromPromise(this.initData());
  }

  private async initData() {
    const { nextRun, previousRun, selectedOption, showRunNowJob } = await this.autoSyncActivitiesDataLayer.init();
    this.selectedOption = INTERVAL_OPTIONS.find((opt) => opt.value === selectedOption) ?? INTERVAL_OPTIONS[0];
    this.nextRun = nextRun;
    this.previousRun = previousRun;
    return { showRunNowJob };
  }

  setSelectedOption(selectedOption: IntervalOptionType) {
    this.selectedOption = selectedOption;
  }

  setIsRunning(val: boolean) {
    this.isRunning = val;
  }

  setIsSaving(val: boolean) {
    this.isSaving = val;
  }

  async runNow() {
    this.setIsRunning(true);
    await this.autoSyncActivitiesDataLayer.runNow();
    this.setIsRunning(false);
  }

  async saveSettings() {
    this.setIsSaving(true);
    const res = await this.autoSyncActivitiesDataLayer.save(this.selectedOption);
    this.nextRun = res.nextRun ?? "";
    this.previousRun = res.previousRun ?? "";
    this.setIsSaving(false);
  }
}
